import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

const Slider = ({ slides, autoPlay = true, interval = 5000 }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(autoPlay);
  const [progress, setProgress] = useState(0);
  const progressRef = useRef(null);
  const startTimeRef = useRef(null);
  const animFrameRef = useRef(null);

  const nextSlide = useCallback(() => {
    setDirection(1);
    setCurrentIndex((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
    setProgress(0);
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    setDirection(-1);
    setCurrentIndex((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
    setProgress(0);
  }, [slides.length]);

  const goToSlide = (index) => {
    setDirection(index > currentIndex ? 1 : -1);
    setCurrentIndex(index);
    setProgress(0);
  };

  // Smooth progress bar via requestAnimationFrame
  useEffect(() => {
    if (!isAutoPlaying) {
      cancelAnimationFrame(animFrameRef.current);
      return;
    }
    setProgress(0);
    startTimeRef.current = null;

    const tick = (timestamp) => {
      if (!startTimeRef.current) startTimeRef.current = timestamp;
      const elapsed = timestamp - startTimeRef.current;
      const pct = Math.min((elapsed / interval) * 100, 100);
      setProgress(pct);
      if (pct < 100) {
        animFrameRef.current = requestAnimationFrame(tick);
      } else {
        nextSlide();
      }
    };
    animFrameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animFrameRef.current);
  }, [isAutoPlaying, currentIndex, interval, nextSlide]);

  const swipePower = (offset, velocity) => Math.abs(offset) * velocity;
  const swipeThreshold = 10000;

  if (!slides || slides.length === 0) return null;

  const slide = slides[currentIndex];

  const slideVariants = {
    enter: (dir) => ({ x: dir > 0 ? '100%' : '-100%', opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir) => ({ x: dir < 0 ? '100%' : '-100%', opacity: 0 }),
  };

  const textVariants = {
    hidden: { opacity: 0, y: 28 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: 0.2 + i * 0.12, duration: 0.6, ease: [0.22, 1, 0.36, 1] },
    }),
  };

  return (
    <div
      className="relative w-full overflow-hidden"
      style={{ height: 'clamp(280px, 55vw, 500px)' }}
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => setIsAutoPlaying(autoPlay)}
    >
      {/* Slides */}
      <AnimatePresence initial={false} custom={direction}>
        <motion.div
          key={currentIndex}
          custom={direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ duration: 0.65, ease: [0.77, 0, 0.18, 1] }}
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.15}
          onDragEnd={(_, { offset, velocity }) => {
            const swipe = swipePower(offset.x, velocity.x);
            if (swipe < -swipeThreshold) nextSlide();
            else if (swipe > swipeThreshold) prevSlide();
          }}
          className="absolute inset-0 cursor-grab active:cursor-grabbing"
        >
          {/* Background Image */}
          <img
            src={slide.image}
            alt={slide.title}
            className="absolute inset-0 w-full h-full object-cover"
            style={{ willChange: 'transform' }}
          />

          {/* Multi-layer overlay for depth */}
          <div className="absolute inset-0" style={{
            background: 'linear-gradient(105deg, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.55) 45%, rgba(0,0,0,0.15) 75%, transparent 100%)'
          }} />
          {/* Bottom vignette */}
          <div className="absolute inset-0" style={{
            background: 'linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 40%)'
          }} />

          {/* Decorative vertical accent line */}
          <motion.div
            initial={{ scaleY: 0, opacity: 0 }}
            animate={{ scaleY: 1, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="absolute left-0 top-0 bottom-0 w-1 origin-top hidden md:block"
            style={{ background: 'linear-gradient(to bottom, #800020, rgba(128,0,32,0.3), transparent)' }}
          />

          {/* Text Content */}
          <div className="absolute inset-0 flex items-center">
            <div className="container-custom w-full px-6 md:px-8 lg:px-12">
              <div className="max-w-xl lg:max-w-2xl pl-4 md:pl-6">

                {/* Badge */}
                {slide.badge && (
                  <motion.div
                    custom={0}
                    variants={textVariants}
                    initial="hidden"
                    animate="visible"
                    className="mb-3 md:mb-4"
                  >
                    <span
                      className="inline-flex items-center gap-1.5 text-xs font-bold tracking-[0.18em] uppercase px-3 py-1.5 rounded-sm"
                      style={{
                        background: 'rgba(128,0,32,0.85)',
                        color: '#fff',
                        backdropFilter: 'blur(8px)',
                        letterSpacing: '0.18em',
                      }}
                    >
                      <span style={{
                        display: 'inline-block', width: 5, height: 5,
                        borderRadius: '50%', background: 'rgba(255,255,255,0.8)'
                      }} />
                      {slide.badge}
                    </span>
                  </motion.div>
                )}

                {/* Title */}
                <motion.h2
                  custom={1}
                  variants={textVariants}
                  initial="hidden"
                  animate="visible"
                  className="font-black text-white leading-none mb-3 md:mb-4"
                  style={{
                    fontSize: 'clamp(1.75rem, 4.5vw, 3.6rem)',
                    letterSpacing: '-0.02em',
                    textShadow: '0 2px 20px rgba(0,0,0,0.4)',
                    fontFamily: "'Barlow Condensed', sans-serif",
                  }}
                >
                  {slide.title}
                </motion.h2>

                {/* Description */}
                <motion.p
                  custom={2}
                  variants={textVariants}
                  initial="hidden"
                  animate="visible"
                  className="text-gray-300 mb-5 md:mb-7 leading-relaxed"
                  style={{
                    fontSize: 'clamp(0.8rem, 1.4vw, 1rem)',
                    maxWidth: '480px',
                    textShadow: '0 1px 8px rgba(0,0,0,0.5)',
                  }}
                >
                  {slide.description}
                </motion.p>

                {/* CTA */}
                {slide.cta && (
                  <motion.div
                    custom={3}
                    variants={textVariants}
                    initial="hidden"
                    animate="visible"
                    className="flex items-center gap-3 flex-wrap"
                  >
                    <a
                      href={slide.cta.link}
                      className="group inline-flex items-center gap-2 font-bold text-sm tracking-wide text-white px-5 py-3 rounded-sm transition-all duration-300"
                      style={{
                        background: '#800020',
                        letterSpacing: '0.06em',
                        boxShadow: '0 4px 24px rgba(128,0,32,0.45)',
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.background = '#9a0025';
                        e.currentTarget.style.boxShadow = '0 6px 32px rgba(128,0,32,0.6)';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.background = '#800020';
                        e.currentTarget.style.boxShadow = '0 4px 24px rgba(128,0,32,0.45)';
                        e.currentTarget.style.transform = '';
                      }}
                    >
                      {slide.cta.text}
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="transition-transform duration-300 group-hover:translate-x-1">
                        <path d="M1 7h12M8 2l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </a>
                  </motion.div>
                )}
              </div>
            </div>
          </div>

          {/* Slide counter — bottom left */}
          <div
            className="absolute bottom-5 left-6 md:left-8 flex items-baseline gap-1"
            style={{ zIndex: 2 }}
          >
            <span className="text-white font-black text-lg" style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '-0.01em' }}>
              {String(currentIndex + 1).padStart(2, '0')}
            </span>
            <span className="text-white/40 text-xs font-light">/ {String(slides.length).padStart(2, '0')}</span>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Navigation arrows */}
      <button
        onClick={prevSlide}
        aria-label="Previous slide"
        className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center transition-all duration-300 rounded-full"
        style={{
          width: 'clamp(36px, 4vw, 44px)',
          height: 'clamp(36px, 4vw, 44px)',
          background: 'rgba(255,255,255,0.12)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.2)',
        }}
        onMouseEnter={e => {
          e.currentTarget.style.background = 'rgba(128,0,32,0.75)';
          e.currentTarget.style.borderColor = 'rgba(128,0,32,0.8)';
          e.currentTarget.style.transform = 'translateY(-50%) scale(1.08)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.background = 'rgba(255,255,255,0.12)';
          e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)';
          e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
        }}
      >
        <ChevronLeftIcon className="text-white" style={{ width: 'clamp(14px,2vw,18px)', height: 'clamp(14px,2vw,18px)' }} />
      </button>
      <button
        onClick={nextSlide}
        aria-label="Next slide"
        className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center transition-all duration-300 rounded-full"
        style={{
          width: 'clamp(36px, 4vw, 44px)',
          height: 'clamp(36px, 4vw, 44px)',
          background: 'rgba(255,255,255,0.12)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.2)',
        }}
        onMouseEnter={e => {
          e.currentTarget.style.background = 'rgba(128,0,32,0.75)';
          e.currentTarget.style.borderColor = 'rgba(128,0,32,0.8)';
          e.currentTarget.style.transform = 'translateY(-50%) scale(1.08)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.background = 'rgba(255,255,255,0.12)';
          e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)';
          e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
        }}
      >
        <ChevronRightIcon className="text-white" style={{ width: 'clamp(14px,2vw,18px)', height: 'clamp(14px,2vw,18px)' }} />
      </button>

      {/* Dot indicators */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center space-x-3 z-10">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => goToSlide(i)}
            aria-label={`Go to slide ${i + 1}`}
            className="group"
          >
            <motion.div
              initial={false}
              animate={{
                width: i === currentIndex ? 40 : 12,
                backgroundColor: i === currentIndex ? '#800020' : 'rgba(255,255,255,0.5)',
              }}
              whileHover={{ scale: 1.2 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="h-2 rounded-full"
            />
          </button>
        ))}
      </div>

      {/* RAF-driven progress bar */}
      {isAutoPlaying && (
        <div
          className="absolute bottom-0 left-0 right-0 z-10"
          style={{ height: '2px', background: 'rgba(255,255,255,0.1)' }}
        >
          <div
            style={{
              height: '100%',
              width: `${progress}%`,
              background: 'linear-gradient(90deg, #800020, #c0002f)',
              transition: 'width 0.05s linear',
            }}
          />
        </div>
      )}

      {/* Google Font */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@700;900&display=swap');
      `}</style>
    </div>
  );
};

export default Slider;